<template>
  <div class="product-form-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <button type="button" class="btn-back" @click="goBack">
          <i class="back-icon">←</i> 返回
        </button>
        <h1 class="page-title">{{ isEditing ? '编辑商品' : '发布新商品' }}</h1>
      </div>
      <div class="header-right">
        <button v-if="!isEditing" @click="goToBatchPublish" class="btn btn-secondary">
          <i class="batch-icon" aria-hidden="true"></i> 批量发布
        </button>
      </div>
    </div>
    
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <span class="loading-text">加载中...</span>
    </div>
    
    <!-- 错误提示 -->
    <div v-if="error && !loading" class="alert alert-danger">
      <i class="alert-icon" aria-hidden="true"></i>
      {{ error }}
    </div>
    
    <!-- 成功提示 -->
    <div v-if="success && !loading" class="alert alert-success">
      <i class="alert-icon" aria-hidden="true"></i>
      {{ isEditing ? '商品更新成功' : '发布商品成功' }}
    </div>
    
    <!-- 表单内容 -->
    <form @submit.prevent="saveProduct" v-if="!loading" class="form-wrapper">
      <!-- 基础信息卡片 -->
      <div class="form-card">
        <div class="card-header">
          <h2 class="card-title">基础信息</h2>
        </div>
        <div class="card-body">
          <div class="form-row">
            <div class="form-group flex-1">
              <label for="name" class="form-label">商品名称 <span class="required">*</span></label>
              <input 
                type="text" 
                id="name" 
                v-model="product.name" 
                required
                class="form-input"
                placeholder="请输入商品名称"
              >
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="category" class="form-label">商品分类 <span class="required">*</span></label>
              <select id="category" v-model="selectedCategoryId" required @change="handleCategoryChange" class="form-select">
                <option value="" disabled>请选择分类</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.name }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="subCategory" class="form-label">商品子分类 <span class="required">*</span></label>
              <select id="subCategory" v-model="product.subCategory" required @change="handleSubCategoryChange" class="form-select">
                <option value="" disabled>请选择子分类</option>
                <option v-for="subCategory in subCategories" :key="subCategory.id" :value="subCategory">
                  {{ subCategory.name }}
                </option>
              </select>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="price" class="form-label">商品价格 <span class="required">*</span></label>
              <div class="input-group">
                <span class="input-prefix">¥</span>
                <input 
                  type="number" 
                  id="price" 
                  v-model="product.price" 
                  min="0" 
                  step="0.01" 
                  required
                  class="form-input price-input"
                  placeholder="0.00"
                >
              </div>
            </div>
            <div class="form-group">
              <label for="stock" class="form-label">库存 <span class="required">*</span></label>
              <input 
                type="number" 
                id="stock" 
                v-model="product.stock" 
                min="0" 
                required
                class="form-input"
                placeholder="0"
              >
            </div>
          </div>
        </div>
      </div>
      
      <!-- 商品图片卡片 -->
      <div class="form-card">
        <div class="card-header">
          <h2 class="card-title">商品主图</h2>
        </div>
        <div class="card-body">
          <div class="form-group">
            <label for="imageUrl" class="form-label">图片URL</label>
            <input 
              type="text" 
              id="imageUrl" 
              v-model="product.imageUrl" 
              class="form-input"
              placeholder="输入图片URL，如：https://example.com/image.jpg"
            >
          </div>
          
          <!-- 图片预览 -->
          <div class="image-preview-section">
            <div class="preview-label">图片预览</div>
            <div class="image-preview-container">
              <img 
                v-if="product.imageUrl" 
                :src="product.imageUrl" 
                :alt="product.name || '商品图片'"
                class="preview-image"
                @error="handleImagePreviewError"
              />
              <div v-else class="preview-placeholder">
                <i class="placeholder-icon">📷</i>
                <span class="placeholder-text">暂无图片</span>
              </div>
            </div>
          </div>
          
          <p class="help-text">在下方的商品详情中可插入更多图片</p>
        </div>
      </div>
      
      <!-- AI试衣配置卡片 -->
      <div class="form-card">
        <div class="card-header">
          <h2 class="card-title">AI试衣配置</h2>
        </div>
        <div class="card-body">
          <div class="try-on-header">
            <div class="checkbox-wrapper">
              <input type="checkbox" id="tryOnEnabled" v-model="tryOnConfig.enabled" class="checkbox-input">
              <label for="tryOnEnabled" class="checkbox-label">支持AI试衣</label>
            </div>
            
            <div v-if="tryOnConfig.enabled" class="garment-type-wrapper">
              <label for="garmentType" class="form-label">服饰类型</label>
              <select id="garmentType" v-model="tryOnConfig.garmentType" class="form-select">
                <option value="TOP">上装</option>
                <option value="BOTTOM">下装</option>
                <option value="DRESS">连衣裙</option>
                <option value="SUIT">套装</option>
              </select>
            </div>
          </div>
          
          <div v-if="tryOnConfig.enabled" class="try-on-fields">
            <div class="form-group">
              <label for="sellerModelId" class="form-label">试衣模型</label>
              <select id="sellerModelId" v-model="tryOnConfig.sellerModelId" class="form-select" required>
                <option value="" disabled>请选择卖家已保存的模型</option>
                <option v-for="model in sellerTryOnModels" :key="model.id" :value="model.id">
                  {{ model.displayName }} - {{ model.provider }} / {{ model.modelCode }}
                </option>
              </select>
              <p v-if="sellerTryOnModels.length === 0" class="help-text">
                暂无可用模型，请先前往
                <router-link to="/seller/dashboard/try-on/models" class="inline-link">AI试衣模型</router-link>
                配置并启用模型，再为商品开启AI试衣。
              </p>
            </div>

            <div class="form-group">
              <label for="garmentImageUrl" class="form-label">试衣服饰图URL</label>
              <input
                type="text"
                id="garmentImageUrl"
                v-model="tryOnConfig.garmentImageUrl"
                class="form-input"
                placeholder="请输入阿里云可访问的公网HTTP/HTTPS图片地址"
              >
            </div>
            
            <!-- 服饰图预览 -->
            <div v-if="tryOnConfig.garmentImageUrl" class="image-preview-section">
              <div class="preview-label">服饰图预览</div>
              <div class="image-preview-container small">
                <img 
                  :src="tryOnConfig.garmentImageUrl" 
                  alt="试衣服饰图"
                  class="preview-image"
                  @error="handleTryOnImageError"
                />
              </div>
            </div>
            
            <div class="info-box">
              <i class="info-icon">ℹ️</i>
              <span>阿里云AI试衣需要公网可访问的服饰图，建议使用平铺或清晰商品图。</span>
            </div>

            <div class="mask-enhancement-card">
              <div class="mask-enhancement-header">
                <div class="checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="maskEnhancement"
                    v-model="maskEnhancementEnabled"
                    @change="handleMaskEnhancementChange"
                    class="checkbox-input"
                  >
                  <label for="maskEnhancement" class="checkbox-label">试衣遮罩增强</label>
                </div>
                <span class="mask-desc">调用阿里云服饰分割API生成精准遮罩，提升试衣效果</span>
              </div>

              <div v-if="maskEnhancementEnabled" class="mask-feather-row">
                <label for="featherRadius" class="form-label">羽化半径</label>
                <div class="feather-control">
                  <input
                    type="range"
                    id="featherRadius"
                    v-model.number="maskFeatherRadius"
                    min="0"
                    max="20"
                    step="1"
                    @change="handleMaskEnhancementChange"
                    class="feather-slider"
                  >
                  <span class="feather-value">{{ maskFeatherRadius }}px</span>
                </div>
                <p class="feather-hint">
                  <span v-if="maskFeatherRadius === 0">硬边：遮罩边缘无过渡，换装区域与保留区域分界明显，可能产生拼接痕迹。</span>
                  <span v-else-if="maskFeatherRadius <= 3">轻微羽化：边缘有微弱过渡，适合轮廓清晰的平铺服装图。</span>
                  <span v-else-if="maskFeatherRadius <= 8">自然过渡（推荐）：遮罩边缘平滑融入，适合大多数真人照片，换装效果最自然。</span>
                  <span v-else>强羽化：边缘柔和范围大，适合复杂纹理或宽松服饰，但过高可能导致颜色渗透到保留区域。</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 尺码管理卡片 -->
      <div v-if="currentCategory && currentCategory.needsSize" class="form-card">
        <div class="card-header">
          <h2 class="card-title">尺码管理</h2>
        </div>
        <div class="card-body">
          <div class="form-group">
            <label for="sizeType" class="form-label">尺码类型</label>
            <select id="sizeType" v-model="selectedSizeTypeId" @change="loadSizes" class="form-select">
              <option value="" disabled>请选择尺码类型</option>
              <option v-for="sizeType in sizeTypes" :key="sizeType.id" :value="sizeType.id">
                {{ sizeType.name }}
              </option>
            </select>
          </div>
          
          <div v-if="sizes.length > 0" class="size-selection">
            <div class="size-grid">
              <div v-for="size in sizes" :key="size.id" class="size-card" :class="{ selected: selectedSizes[size.id] }">
                <label class="size-checkbox-wrapper">
                  <input 
                    type="checkbox" 
                    v-model="selectedSizes[size.id]" 
                    @change="handleSizeSelection(size.id)"
                    class="size-checkbox"
                  >
                  <span class="size-name">{{ size.name }}</span>
                </label>
                
                <div v-if="selectedSizes[size.id]" class="size-params">
                    <div class="params-grid">
                      <div class="param-item">
                        <label>胸围(cm)</label>
                        <input type="number" v-model="sizeParams[size.id].chest" step="0.1" min="0" placeholder="88">
                      </div>
                      <div class="param-item">
                        <label>腰围(cm)</label>
                        <input type="number" v-model="sizeParams[size.id].waist" step="0.1" min="0" placeholder="76">
                      </div>
                      <div class="param-item">
                        <label>臀围(cm)</label>
                        <input type="number" v-model="sizeParams[size.id].hips" step="0.1" min="0" placeholder="90">
                      </div>
                      <div class="param-item">
                        <label>肩宽(cm)</label>
                        <input type="number" v-model="sizeParams[size.id].shoulderWidth" step="0.1" min="0" placeholder="42">
                      </div>
                      <div class="param-item">
                        <label>库存</label>
                        <input type="number" v-model="sizeParams[size.id].stock" step="1" min="0" placeholder="10">
                      </div>
                      <div class="param-item">
                        <label>附加价格</label>
                        <div class="param-input-group">
                          <span class="param-prefix">¥</span>
                          <input type="number" v-model="sizeParams[size.id].extraPrice" step="0.01" min="0" placeholder="0">
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 商品详情卡片 -->
      <div class="form-card">
        <div class="card-header">
          <h2 class="card-title">商品详情</h2>
        </div>
        <div class="card-body">
          <div class="form-group">
            <label class="form-label">商品描述</label>
            <RichTextEditor v-model="product.description" label="" />
          </div>
        </div>
      </div>
      
      <!-- 表单操作按钮 -->
      <div class="form-actions">
        <button type="button" class="btn btn-outline" @click="goBack">取消</button>
        <button type="submit" class="btn btn-primary" :disabled="submitting">
          <span v-if="submitting" class="btn-loading">
            <span class="loading-dots"></span>
          </span>
          {{ submitting ? (isEditing ? '更新中...' : '发布中...') : (isEditing ? '更新商品' : '发布商品') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import RichTextEditor from '../components/RichTextEditor.vue'

export default {
  name: 'ProductForm',
  components: {
    RichTextEditor
  },
  data() {
    return {
      product: {
        name: '',
        price: 0,
        imageUrl: '',
        description: '',
        stock: 1,
        subCategory: null
      },
      isEditing: false,
      loading: true,
      submitting: false,
      error: '',
      success: false,
      categories: [],
      subCategories: [],
      selectedCategoryId: null,
      tryOnConfig: {
        enabled: false,
        garmentImageUrl: '',
        garmentType: 'TOP',
        sellerModelId: ''
      },
      sellerTryOnModels: [],
      maskEnhancementEnabled: false,
      maskFeatherRadius: 5,
      sizeTypes: [],
      sizes: [],
      selectedSizeTypeId: null,
      selectedSizes: {},
      sizeParams: {}
    }
  },
  created() {
    const productId = this.$route.query.id
    if (productId) {
      this.isEditing = true
      Promise.all([this.fetchCategories(), this.fetchSizeTypes().catch(err => console.error('获取尺码类型失败，继续加载其他内容', err)), this.fetchProduct(productId).catch(err => console.error('获取商品信息失败，继续加载其他内容', err))])
        .finally(() => {
          this.loading = false
        })
    } else {
      Promise.all([this.fetchCategories(), this.fetchSizeTypes().catch(err => console.error('获取尺码类型失败，继续加载其他内容', err))])
        .finally(() => {
          this.loading = false
        })
    }
  },
  mounted() {
    this.fetchSellerTryOnModels()
  },
  watch: {
    selectedCategoryId(newVal) {
      this.loadSubCategories(newVal)
    },
    'tryOnConfig.sellerModelId'(newVal) {
      this.loadMaskEnhancementFromModel(newVal)
    }
  },
  computed: {
    // 计算当前选中的分类
    currentCategory() {
      return this.categories.find(cat => cat.id === this.selectedCategoryId) || null
    }
  },
  methods: {
    async fetchCategories() {
      try {
        const response = await this.$axios.get('/categories/active')
        // 正确访问嵌套在data字段中的分类列表
        this.categories = response.data.data || []
        console.log('Categories:', this.categories)
        console.log('Categories with needsSize:', this.categories.map(cat => ({id: cat.id, name: cat.name, needsSize: cat.needsSize})))
        // 如果有分类且不是编辑模式，自动选择第一个分类
        if (this.categories.length > 0 && !this.isEditing && !this.selectedCategoryId) {
          this.selectedCategoryId = this.categories[0].id
          console.log('Auto selected category:', this.selectedCategoryId)
        }
      } catch (err) {
        console.error('获取分类信息失败', err)
      }
    },
    
    async fetchSizeTypes() {
      try {
        console.log('Fetching size types...')
        const response = await this.$axios.get('/size-types/active')
        console.log('Response:', response)
        this.sizeTypes = response.data.data || response.data || []
        console.log('Size types:', this.sizeTypes)
        if (this.sizeTypes.length > 0 && !this.selectedSizeTypeId) {
          this.selectedSizeTypeId = this.sizeTypes[0].id
          this.loadSizes()
        }
      } catch (err) {
        console.error('获取尺码类型失败', err)
      }
    },
    
    async loadSizes() {
      if (!this.selectedSizeTypeId) {
        this.sizes = []
        return
      }
      try {
        const response = await this.$axios.get(`/sizes/size-type/${this.selectedSizeTypeId}`)
        this.sizes = response.data.data || response.data || []
        console.log('Sizes:', this.sizes)
        // 初始化尺码选择状态和默认数据
        this.sizes.forEach(size => {
          this.selectedSizes[size.id] = false
          this.sizeParams[size.id] = this.getDefaultSizeParams(size.name)
        })
      } catch (err) {
        console.error('获取尺码失败', err)
      }
    },
    
    // 根据尺码名称返回默认参数（参考市面上主流男装/女装尺码标准）
    getDefaultSizeParams(sizeName) {
      // 移除尺码名称中的"码"字，支持"S码"和"S"两种格式
      const cleanSizeName = sizeName.replace('码', '')
      
      // 女装标准尺码（GB/T 1335.2-2008）
      const womenSizes = {
        'XXS': { chest: 76, waist: 56, hips: 82, shoulderWidth: 35, stock: 3, extraPrice: 0 },
        'XS': { chest: 80, waist: 60, hips: 86, shoulderWidth: 36.5, stock: 5, extraPrice: 0 },
        'S': { chest: 84, waist: 64, hips: 90, shoulderWidth: 38, stock: 10, extraPrice: 0 },
        'M': { chest: 88, waist: 68, hips: 94, shoulderWidth: 39.5, stock: 15, extraPrice: 0 },
        'L': { chest: 92, waist: 72, hips: 98, shoulderWidth: 41, stock: 12, extraPrice: 0 },
        'XL': { chest: 96, waist: 76, hips: 102, shoulderWidth: 42.5, stock: 8, extraPrice: 0 },
        'XXL': { chest: 100, waist: 80, hips: 106, shoulderWidth: 44, stock: 5, extraPrice: 0 },
        'XXXL': { chest: 104, waist: 84, hips: 110, shoulderWidth: 45.5, stock: 3, extraPrice: 10 },
        '4XL': { chest: 108, waist: 88, hips: 114, shoulderWidth: 47, stock: 2, extraPrice: 20 }
      }
      
      // 男装标准尺码（GB/T 1335.1-2008）
      const menSizes = {
        'XXS': { chest: 80, waist: 62, hips: 84, shoulderWidth: 37, stock: 2, extraPrice: 0 },
        'XS': { chest: 84, waist: 66, hips: 88, shoulderWidth: 38.5, stock: 4, extraPrice: 0 },
        'S': { chest: 88, waist: 70, hips: 92, shoulderWidth: 40, stock: 8, extraPrice: 0 },
        'M': { chest: 92, waist: 74, hips: 96, shoulderWidth: 41.5, stock: 15, extraPrice: 0 },
        'L': { chest: 96, waist: 78, hips: 100, shoulderWidth: 43, stock: 15, extraPrice: 0 },
        'XL': { chest: 100, waist: 82, hips: 104, shoulderWidth: 44.5, stock: 12, extraPrice: 0 },
        'XXL': { chest: 104, waist: 86, hips: 108, shoulderWidth: 46, stock: 8, extraPrice: 0 },
        'XXXL': { chest: 108, waist: 90, hips: 112, shoulderWidth: 47.5, stock: 5, extraPrice: 10 },
        '4XL': { chest: 112, waist: 94, hips: 116, shoulderWidth: 49, stock: 3, extraPrice: 20 },
        '5XL': { chest: 116, waist: 98, hips: 120, shoulderWidth: 50.5, stock: 2, extraPrice: 30 },
        '6XL': { chest: 120, waist: 102, hips: 124, shoulderWidth: 52, stock: 2, extraPrice: 40 }
      }
      
      // 童装标准尺码
      const kidsSizes = {
        '90': { chest: 52, waist: 48, hips: 54, shoulderWidth: 24, stock: 8, extraPrice: 0 },
        '100': { chest: 56, waist: 52, hips: 58, shoulderWidth: 26, stock: 10, extraPrice: 0 },
        '110': { chest: 60, waist: 56, hips: 62, shoulderWidth: 28, stock: 10, extraPrice: 0 },
        '120': { chest: 64, waist: 60, hips: 66, shoulderWidth: 30, stock: 10, extraPrice: 0 },
        '130': { chest: 68, waist: 64, hips: 70, shoulderWidth: 32, stock: 8, extraPrice: 0 },
        '140': { chest: 72, waist: 68, hips: 74, shoulderWidth: 34, stock: 6, extraPrice: 0 },
        '150': { chest: 76, waist: 72, hips: 78, shoulderWidth: 36, stock: 4, extraPrice: 0 },
        '160': { chest: 80, waist: 76, hips: 82, shoulderWidth: 38, stock: 3, extraPrice: 0 }
      }
      
      // 通用尺码（优先尝试男装，再尝试女装，最后尝试童装）
      return menSizes[cleanSizeName] || womenSizes[cleanSizeName] || kidsSizes[cleanSizeName] || 
             { chest: 88, waist: 70, hips: 92, shoulderWidth: 40, stock: 10, extraPrice: 0 }
    },
    
    async loadSubCategories(categoryId) {
      if (!categoryId) {
        this.subCategories = []
        this.product.subCategory = null
        return
      }
      try {
        const response = await this.$axios.get(`/sub-categories/category/${categoryId}`)
        // SubCategoryController直接返回列表，不包含data字段
        this.subCategories = Array.isArray(response.data) ? response.data : []
        // 匹配已保存的子分类，若无则自动选择第一个
        if (this.subCategories.length > 0) {
          const savedId = this.product.subCategory ? this.product.subCategory.id : null
          const matched = savedId ? this.subCategories.find(s => s.id === savedId) : null
          this.product.subCategory = matched || this.subCategories[0]
        }
      } catch (err) {
        console.error('获取子分类信息失败', err)
      }
    },
    
    handleCategoryChange() {
      console.log('Category changed:', this.selectedCategoryId)
      console.log('Current category:', this.currentCategory)
      console.log('Current category needsSize:', this.currentCategory ? this.currentCategory.needsSize : 'No category selected')
      // 如果当前分类需要尺码功能，加载尺码类型
      if (this.currentCategory && this.currentCategory.needsSize) {
        this.fetchSizeTypes()
      } else {
        // 重置尺码相关数据
        this.sizeTypes = []
        this.sizes = []
        this.selectedSizeTypeId = null
        this.selectedSizes = {}
        this.sizeParams = {}
      }
    },
    
    handleSubCategoryChange() {
      // 子分类变化时的处理
      console.log('SubCategory changed:', this.product.subCategory)
    },
    
    handleSizeSelection(sizeId) {
      if (!this.selectedSizes[sizeId]) {
        // 取消选择时重置参数为默认值
        const size = this.sizes.find(s => s.id === sizeId)
        this.sizeParams[sizeId] = size ? this.getDefaultSizeParams(size.name) : { chest: 88, waist: 72, hips: 90, shoulderWidth: 42, stock: 10, extraPrice: 0 }
      }
    },
    
    async fetchProduct(id) {
      try {
        const response = await this.$axios.get(`/products/${id}`)
        // 正确访问嵌套在data字段中的商品数据
        const productData = response.data.data || response.data
        // 合并响应数据到现有product对象以保留stock等属性的响应式特性
        this.product = Object.assign({}, this.product, productData)
        await this.fetchTryOnConfig(this.product.id)
        // 如果商品有二级分类，设置选中的分类
        if (this.product.subCategory && this.product.subCategory.category) {
          this.selectedCategoryId = this.product.subCategory.category.id
          // 等待分类列表加载完成后再加载二级分类
          await this.$nextTick()
          this.loadSubCategories(this.selectedCategoryId)
        }
        this.error = ''
      } catch (err) {
        this.error = '获取商品信息失败'
        console.error(err)
      }
    },

    async fetchTryOnConfig(productId) {
      try {
        const response = await this.$axios.get(`/seller/try-on/products/${productId}/config`)
        this.tryOnConfig = Object.assign({}, this.tryOnConfig, response.data, {
          sellerModelId: response.data.sellerModelId || ''
        })
      } catch (err) {
        console.error('获取AI试衣配置失败', err)
      }
    },

    async fetchSellerTryOnModels() {
      try {
        const response = await this.$axios.get('/seller/try-on/models')
        this.sellerTryOnModels = Array.isArray(response.data)
          ? response.data.filter(model => model.enabled && model.status === 'ACTIVE')
          : []
        if (this.tryOnConfig.sellerModelId) {
          this.loadMaskEnhancementFromModel(this.tryOnConfig.sellerModelId)
        }
      } catch (err) {
        console.error('获取卖家AI试衣模型失败', err)
        this.sellerTryOnModels = []
      }
    },

    loadMaskEnhancementFromModel(modelId) {
      if (!modelId) {
        this.maskEnhancementEnabled = false
        this.maskFeatherRadius = 5
        return
      }
      const model = this.sellerTryOnModels.find(m => m.id === modelId)
      if (!model || !model.extraConfigJson) {
        this.maskEnhancementEnabled = false
        this.maskFeatherRadius = 5
        return
      }
      try {
        const config = JSON.parse(model.extraConfigJson)
        const enhancement = config.maskEnhancement
        if (enhancement) {
          this.maskEnhancementEnabled = enhancement.enabled === true
          this.maskFeatherRadius = typeof enhancement.featherRadius === 'number'
            ? Math.max(0, Math.min(enhancement.featherRadius, 20))
            : 5
        } else {
          this.maskEnhancementEnabled = false
          this.maskFeatherRadius = 5
        }
      } catch (e) {
        this.maskEnhancementEnabled = false
        this.maskFeatherRadius = 5
      }
    },

    async handleMaskEnhancementChange() {
      const modelId = this.tryOnConfig.sellerModelId
      if (!modelId) return
      const model = this.sellerTryOnModels.find(m => m.id === modelId)
      if (!model) return

      let config = {}
      try {
        if (model.extraConfigJson) {
          config = JSON.parse(model.extraConfigJson)
        }
      } catch (e) {
        config = {}
      }

      config.maskEnhancement = {
        enabled: this.maskEnhancementEnabled,
        featherRadius: this.maskFeatherRadius
      }

      const newExtraConfigJson = JSON.stringify(config)
      try {
        await this.$axios.put(`/seller/try-on/models/${modelId}`, {
          provider: model.provider,
          displayName: model.displayName,
          region: model.region,
          endpoint: model.endpoint,
          modelCode: model.modelCode,
          authType: model.authType,
          capabilitiesJson: model.capabilitiesJson,
          extraConfigJson: newExtraConfigJson,
          timeoutSeconds: model.timeoutSeconds,
          enabled: model.enabled
        })
        model.extraConfigJson = newExtraConfigJson
      } catch (err) {
        console.error('保存遮罩增强配置失败', err)
      }
    },

    goToBatchPublish() {
      this.$router.push('/seller/dashboard/product/batch')
    },
    
    // 返回上一页
    goBack() {
      this.$router.push('/seller/dashboard')
    },
    
    async saveProduct() {
      try {
        this.submitting = true
        this.error = ''
        
        const sellerId = localStorage.getItem('sellerId')
        if (!sellerId) {
          this.error = '请先登录'
          return
        }
        
        // 添加商户ID到商品数据
        const productData = {
          ...this.product,
          seller: {
            id: parseInt(sellerId)
          }
        }
        
        let productId = this.product.id
        if (this.isEditing) {
          const response = await this.$axios.put(`/products/${this.product.id}`, productData)
          productId = response.data.id || this.product.id
          await this.saveTryOnConfig(productId)
        } else {
          const response = await this.$axios.post('/products', productData)
          productId = response.data.id
          await this.saveTryOnConfig(productId)
        }
        
        // 保存尺码信息（失败不影响商品发布）
        if (this.currentCategory && this.currentCategory.needsSize && productId) {
          try {
            await this.saveSizeDetails(productId)
          } catch (sizeErr) {
            console.error('保存尺码信息失败，但商品已创建成功:', sizeErr)
          }
        }
        
        await this.triggerTranslation(productId)
        
        this.success = true
        setTimeout(() => {
          this.$router.push('/seller/dashboard')
        }, 1500)
      } catch (err) {
        this.error = (this.isEditing ? '更新商品失败' : '发布商品失败') + ': ' + (err.response?.data?.message || err.message || '未知错误')
        console.error(err)
      } finally {
        this.submitting = false
      }
    },
    
    // 图片预览错误处理
    handleImagePreviewError(event) {
      event.target.style.display = 'none'
      const container = event.target.parentElement
      container.innerHTML = `
        <div class="preview-placeholder">
          <i class="placeholder-icon" aria-hidden="true"></i>
          <span class="placeholder-text">图片加载失败</span>
        </div>
      `
    },
    
    // AI试衣图片错误处理
    handleTryOnImageError(event) {
      event.target.style.display = 'none'
      const container = event.target.parentElement
      container.innerHTML = `
        <div class="preview-placeholder">
          <i class="placeholder-icon" aria-hidden="true"></i>
          <span class="placeholder-text">图片加载失败</span>
        </div>
      `
    },
    
    async triggerTranslation(productId) {
      try {
        await this.$axios.post(`/api/products/${productId}/translate`)
        await this.$axios.post(`/api/products/${productId}/summarize`)
        console.log('Translation tasks triggered for product:', productId)
      } catch (err) {
        console.error('Failed to trigger translation:', err)
      }
    },

    async saveTryOnConfig(productId) {
      if (!productId) return
      await this.$axios.put(`/seller/try-on/products/${productId}/config`, {
        enabled: this.tryOnConfig.enabled,
        garmentImageUrl: this.tryOnConfig.garmentImageUrl,
        garmentType: this.tryOnConfig.garmentType,
        sellerModelId: this.tryOnConfig.sellerModelId
      })
    },
    
    async saveSizeDetails(productId) {
      try {
        // 收集选中的尺码
        const selectedSizeIds = Object.keys(this.selectedSizes).filter(sizeId => this.selectedSizes[sizeId])
        console.log('选中的尺码ID:', selectedSizeIds)
        
        for (const sizeId of selectedSizeIds) {
          const params = this.sizeParams[sizeId]
          console.log('尺码ID', sizeId, '的参数:', params)
          const sizeDetail = {
            size: { id: parseInt(sizeId) },
            chest: params.chest,
            waist: params.waist,
            hips: params.hips,
            shoulderWidth: params.shoulderWidth,
            stock: params.stock,
            extraPrice: params.extraPrice
          }
          console.log('准备发送的尺码详情:', sizeDetail)
          
          await this.$axios.post(`/products/${productId}/sizes`, sizeDetail)
        }
      } catch (err) {
        console.error('保存尺码信息失败', err)
        throw err
      }
    }
  }
}
</script>

<style scoped>
.product-form-container {
  padding: 1.5rem;
  max-width: 900px;
  margin: 0 auto;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #ff4400;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  background-color: transparent;
  border: 1px solid #ddd;
  border-radius: 6px;
  color: #666;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-back:hover {
  background-color: #f5f5f5;
  border-color: #ff4400;
  color: #ff4400;
}

.back-icon {
  font-size: 1rem;
}

.page-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.header-right {
  display: flex;
  gap: 1rem;
}

/* 加载状态 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #ff4400;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 1rem;
  color: #999;
  font-size: 0.95rem;
}

/* 警告提示 */
.alert {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-radius: 6px;
  margin-bottom: 1.5rem;
}

.alert-danger {
  background-color: #fff5f5;
  border: 1px solid #ffccc7;
  color: #f5222d;
}

.alert-success {
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  color: #52c41a;
}

.alert-icon {
  position: relative;
  width: 18px;
  height: 18px;
  font-size: 0;
  display: inline-flex;
  flex: 0 0 18px;
}

.alert-icon::before,
.alert-icon::after {
  content: '';
  position: absolute;
}

.alert-danger .alert-icon::before,
.alert-danger .alert-icon::after {
  top: 8px;
  left: 3px;
  width: 12px;
  height: 2px;
  background: currentColor;
}

.alert-danger .alert-icon::before {
  transform: rotate(45deg);
}

.alert-danger .alert-icon::after {
  transform: rotate(-45deg);
}

.alert-success .alert-icon::before {
  top: 5px;
  left: 4px;
  width: 8px;
  height: 4px;
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(-45deg);
}

/* 表单包装器 */
.form-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* 表单卡片 */
.form-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.card-header {
  padding: 1rem 1.5rem;
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.card-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-icon {
  font-size: 1.25rem;
}

.card-body {
  padding: 1.5rem;
}

/* 表单行 */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* 表单组 */
.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.flex-1 {
  grid-column: span 2;
}

@media (max-width: 768px) {
  .form-group.flex-1 {
    grid-column: span 1;
  }
}

.form-label {
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
  font-size: 0.95rem;
}

.required {
  color: #ff4400;
}

/* 表单输入 */
.form-input {
  padding: 0.75rem 1rem;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background-color: white;
}

.form-input:focus {
  outline: none;
  border-color: #ff4400;
  box-shadow: 0 0 0 3px rgba(255, 68, 0, 0.1);
}

.form-input::placeholder {
  color: #bbb;
}

/* 输入组 */
.input-group {
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  overflow: hidden;
}

.input-prefix {
  padding: 0.75rem 0.875rem;
  background-color: #f5f5f5;
  color: #666;
  font-size: 0.95rem;
  border-right: 1px solid #d9d9d9;
}

.price-input {
  border: none;
  border-radius: 0;
}

/* 表单选择 */
.form-select {
  padding: 0.75rem 1rem;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 0.95rem;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-select:focus {
  outline: none;
  border-color: #ff4400;
  box-shadow: 0 0 0 3px rgba(255, 68, 0, 0.1);
}

/* 图片预览区域 */
.image-preview-section {
  margin-top: 1rem;
}

.preview-label {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.image-preview-container {
  width: 200px;
  height: 200px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
}

.image-preview-container.small {
  width: 150px;
  height: 150px;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #999;
}

.placeholder-icon {
  position: relative;
  width: 28px;
  height: 28px;
  font-size: 0;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(148, 163, 184, 0.12);
}

.placeholder-icon::before,
.placeholder-icon::after {
  content: '';
  position: absolute;
  top: 13px;
  left: 6px;
  width: 16px;
  height: 2px;
  background: #94a3b8;
}

.placeholder-icon::before {
  transform: rotate(45deg);
}

.placeholder-icon::after {
  transform: rotate(-45deg);
}

.placeholder-text {
  font-size: 0.875rem;
}

/* 帮助文本 */
.help-text {
  font-size: 0.825rem;
  color: #999;
  margin-top: 0.5rem;
}

.help-text .inline-link {
  color: #1976d2;
  text-decoration: underline;
}

/* 复选框组 */
.checkbox-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.checkbox-input {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #ff4400;
  border-radius: 4px;
}

.checkbox-input:hover {
  box-shadow: 0 0 0 3px rgba(255, 68, 0, 0.1);
}

.checkbox-label {
  font-size: 1rem;
  color: #334155;
  cursor: pointer;
  font-weight: 600;
  padding: 0.25rem 0;
}

/* AI试衣头部布局 */
.try-on-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.checkbox-input {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #ff4400;
  border-radius: 4px;
}

.checkbox-input:hover {
  box-shadow: 0 0 0 3px rgba(255, 68, 0, 0.1);
}

.checkbox-label {
  font-size: 1rem;
  color: #334155;
  cursor: pointer;
  font-weight: 600;
  padding: 0.25rem 0;
}

.garment-type-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.garment-type-wrapper .form-label {
  margin-bottom: 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: #64748b;
}

/* AI试衣字段 */
.try-on-fields {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

/* AI试衣服饰类型选择框 */
.try-on-fields .form-select {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.875rem 1.25rem;
  font-size: 1rem;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  transition: all 0.25s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 16px;
}

.try-on-fields .form-select:hover {
  border-color: #ff4400;
  box-shadow: 0 4px 12px rgba(255, 68, 0, 0.1);
}

.try-on-fields .form-select:focus {
  border-color: #ff4400;
  box-shadow: 0 0 0 4px rgba(255, 68, 0, 0.15);
  outline: none;
}

.try-on-fields .form-select:active {
  transform: scale(0.99);
}

/* 信息提示框 */
.info-box {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #ad8b00;
}

.info-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

/* 尺码选择 */
.size-selection {
  margin-top: 1rem;
}

.size-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.size-card {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 1rem;
  background-color: white;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.size-card.selected {
  border-color: #ff4400;
  background-color: #fff8f5;
}

.size-checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.size-checkbox {
  width: 18px;
  height: 18px;
}

.size-name {
  font-weight: 600;
  color: #333;
}

.size-params {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px dashed #e5e5e5;
}

.params-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

@media (max-width: 640px) {
  .params-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.param-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.param-item label {
  font-size: 0.775rem;
  color: #666;
  font-weight: 500;
}

.param-item input {
  padding: 0.375rem 0.5rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 0.825rem;
  width: 100%;
  box-sizing: border-box;
}

.param-input-group {
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
}

.param-prefix {
  padding: 0.5rem 0.625rem;
  background-color: #f5f5f5;
  color: #666;
  font-size: 0.875rem;
  border-right: 1px solid #d9d9d9;
}

.param-input-group input {
  border: none;
  border-radius: 0;
}

/* 富文本编辑器 */
:deep(.rich-text-editor) {
  min-height: 250px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
}

/* 表单操作 */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* 按钮样式 */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-primary {
  background-color: #ff4400;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #e63d00;
  box-shadow: 0 2px 8px rgba(255, 68, 0, 0.3);
}

.btn-primary:disabled {
  background-color: #ffaa80;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #2563eb;
  color: white;
}

.btn-secondary:hover {
  background-color: #1d4ed8;
}

.btn-outline {
  background-color: transparent;
  border: 1px solid #d9d9d9;
  color: #666;
}

.btn-outline:hover {
  background-color: #f5f5f5;
  border-color: #ff4400;
  color: #ff4400;
}

/* 按钮加载状态 */
.btn-loading {
  display: inline-block;
  width: 16px;
  height: 16px;
}

.loading-dots {
  display: inline-flex;
  gap: 2px;
}

.loading-dots::before,
.loading-dots::after {
  content: '';
  width: 4px;
  height: 4px;
  background-color: currentColor;
  border-radius: 50%;
  animation: loadingDot 1s infinite;
}

.loading-dots::after {
  animation-delay: 0.2s;
}

@keyframes loadingDot {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

/* 批量图标 */
.batch-icon {
  position: relative;
  width: 18px;
  height: 18px;
  display: inline-block;
  margin-right: 0.45rem;
  vertical-align: -3px;
  font-size: 0;
}

.batch-icon::before,
.batch-icon::after {
  content: '';
  position: absolute;
}

.batch-icon::before {
  inset: 2px;
  border: 2px solid currentColor;
  border-radius: 4px;
}

.batch-icon::after {
  top: 8px;
  left: 2px;
  width: 14px;
  height: 2px;
  background: currentColor;
}

/* 遮罩增强卡片 */
.mask-enhancement-card {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f0f7ff;
  border: 1px solid #b3d8ff;
  border-radius: 8px;
}

.mask-enhancement-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.mask-enhancement-header .checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.mask-enhancement-header .checkbox-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a3a5c;
  cursor: pointer;
}

.mask-desc {
  font-size: 0.825rem;
  color: #4a7aaa;
}

.mask-feather-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #cce0ff;
}

.mask-feather-row .form-label {
  font-size: 0.875rem;
  color: #334155;
  margin-bottom: 0;
  flex-shrink: 0;
}

.feather-control {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.feather-slider {
  flex: 1;
  max-width: 200px;
  height: 6px;
  accent-color: #ff4400;
  cursor: pointer;
}

.feather-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #ff4400;
  min-width: 36px;
}

.feather-hint {
  margin: 0.25rem 0 0 0;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.4;
}

.product-form-container {
  position: relative;
}

.page-header {
  align-items: flex-end;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page-title {
  letter-spacing: -0.04em;
}

.form-wrapper {
  gap: 1.2rem;
}

.form-card {
  overflow: hidden;
}

.card-body {
  display: grid;
  gap: 1rem;
}

.image-preview-container,
.preview-placeholder,
.mask-enhancement-card,
.info-box,
.size-card,
.form-actions {
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.05);
}

.image-preview-container,
.mask-enhancement-card,
.size-card,
.form-actions {
  border-radius: 24px;
}

.preview-placeholder {
  border-radius: 20px;
}

.preview-image {
  transition: transform var(--duration-base) var(--ease-standard);
}

.image-preview-container:hover .preview-image {
  transform: scale(1.02);
}

.try-on-header,
.mask-enhancement-header,
.feather-control {
  align-items: center;
}

.checkbox-wrapper,
.size-checkbox-wrapper {
  padding: 0.25rem 0;
}

.size-card.selected {
  border-color: rgba(0, 113, 227, 0.18);
  box-shadow: 0 0 0 1px rgba(0, 113, 227, 0.1), 0 18px 36px rgba(0, 113, 227, 0.08);
}

.form-actions {
  position: sticky;
  bottom: 12px;
  z-index: 5;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.btn-primary {
  background: linear-gradient(135deg, #111113, #2c2c2e);
}

.btn-secondary {
  background: rgba(15, 23, 42, 0.06);
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.btn-outline {
  border-color: var(--color-border);
  color: var(--color-text-soft);
}

.btn-outline:hover {
  border-color: rgba(0, 113, 227, 0.18);
  color: var(--color-accent-strong);
  background: rgba(0, 113, 227, 0.05);
}
</style>
